/**
 * ApiKeyCard — R3-S3 (F3.4)
 *
 * "Your API-Key" im Developer-Bereich (v1-Paritaet zu
 * app/views/home/api.haml Zeile 12-15): maskierte Anzeige mit Reveal,
 * Copy-Button, Sicherheitshinweis und curl-Beispiel fuer die Public API.
 *
 * Zusaetzlich der Hinweis, dass die im Developer-Bereich erzeugten API-Tokens
 * seit Runde 3 gleichwertig als "Api-Key" gegen die Public API funktionieren
 * und auf dasselbe Monatskontingent einzahlen.
 */
import { useState, useCallback, useMemo } from 'react'
import { Check, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ApiKeyField } from '../../components/ApiKeyField'
import { useApiUsage } from './useApiUsage'
import { buildPublicApiCurl } from './v1Endpoints'

const KEY_PLACEHOLDER = 'YOUR_API_KEY'

export function ApiKeyCard() {
  const { t } = useTranslation('developer')
  const { usage, isLoading } = useApiUsage()
  const [copied, setCopied] = useState(false)

  // Root-Cause-Fix (heliumtracker-web#21): Tarife ohne Public-API-Zugang
  // duerfen den Key nicht zeigen — konsistent mit der Sperre der API-Tokens.
  const apiAccess = usage?.api_access ?? false
  const apiKey = usage?.api_key ?? null
  const hasKey = apiAccess && apiKey != null && apiKey.length > 0

  const displayCurl = useMemo(() => buildPublicApiCurl(KEY_PLACEHOLDER), [])

  const handleCopyCurl = useCallback(async () => {
    const snippet = buildPublicApiCurl(hasKey ? apiKey : KEY_PLACEHOLDER)
    try {
      await navigator.clipboard.writeText(snippet)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard-API nicht verfügbar
    }
  }, [apiKey, hasKey])

  return (
    <section
      className="card card-pad"
      data-testid="developer-api-key-card"
      aria-label={t('apiKeyCard.title')}
      style={{ marginBottom: 18 }}
    >
      <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>{t('apiKeyCard.title')}</h2>

      {isLoading && (
        <p className="muted" style={{ fontSize: 13, margin: 0 }} data-testid="developer-api-key-loading">
          {t('apiKeyCard.loading')}
        </p>
      )}

      {!isLoading && !apiAccess && (
        <p className="muted" style={{ fontSize: 13, margin: 0 }} data-testid="developer-api-key-no-access">
          {t('apiQuota.noAccess')}
        </p>
      )}

      {!isLoading && apiAccess && !hasKey && (
        <p className="muted" style={{ fontSize: 13, margin: 0 }} data-testid="developer-api-key-empty">
          {t('apiKeyCard.noKey')}
        </p>
      )}

      {!isLoading && hasKey && (
        <ApiKeyField
          apiKey={apiKey}
          maskedValue={usage?.api_key_masked}
          testIdPrefix="developer-api-key"
          labels={{
            ariaLabel: t('apiKeyCard.ariaLabel'),
            show: t('apiKeyCard.show'),
            hide: t('apiKeyCard.hide'),
            showAria: t('apiKeyCard.showAria'),
            hideAria: t('apiKeyCard.hideAria'),
            copy: t('apiKeyCard.copy'),
            copyAria: t('apiKeyCard.copyAria'),
          }}
        />
      )}

      {apiAccess && (
        <>
          <p
            data-testid="developer-api-key-security-hint"
            style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-subtle)' }}
          >
            {t('apiKeyCard.securityHint')}
          </p>

          <p
            data-testid="developer-api-key-token-hint"
            style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text-subtle)' }}
          >
            {t('apiKeyCard.tokenHint')}
          </p>
        </>
      )}

      {apiAccess && (
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{t('apiKeyCard.curlTitle')}</h3>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => { void handleCopyCurl() }}
            data-testid="developer-api-key-copy-curl"
            aria-label={t('apiKeyCard.copyCurlAria')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
            {copied ? t('apiKeyCard.copied') : t('apiKeyCard.copyCurl')}
          </button>
        </div>
        <pre
          data-testid="developer-api-key-curl"
          style={{
            margin: 0,
            padding: 12,
            backgroundColor: 'var(--bg-elev)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 11,
            fontFamily: 'monospace',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {displayCurl}
        </pre>
        <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-subtle)' }}>
          {t('apiKeyCard.curlHint')}
        </p>
      </div>
      )}
    </section>
  )
}
